import { RUN_TIME_BLOCK_KEY } from '../../const';

const SEPARATOR = '_';

export interface ParsedRuntimeBlockId {
  isInstance: boolean;
  originalBlockId: string;
  instanceKey: string | undefined;
}

/**
 * Parses a runtime block ID like `runTimeBlockKey_<originalBlockId>_<instanceKey>` into
 * its parts. Mirrors `getOriginalBlockId` (runTimeBlocks.ts).
 *
 * Convention: block IDs are auto-generated as `b_<single-segment>` (no `_` inside the
 * segment). The boundary between original block id and instance key is therefore the
 * SECOND `_` (the one right after the `b_<X>` prefix). Everything past that boundary is
 * the instance key chain, which CAN contain `_` (entity-id primary keys like `e_<uuid>`,
 * and nested chains joined by `_`).
 */
export function parseRuntimeBlockId(blockId: string): ParsedRuntimeBlockId {
  if (!blockId.startsWith(RUN_TIME_BLOCK_KEY + SEPARATOR)) {
    return {
      isInstance: false,
      originalBlockId: blockId,
      instanceKey: undefined,
    };
  }

  const prefixEnd = blockId.indexOf(SEPARATOR);
  const blockIdInnerSep = blockId.indexOf(SEPARATOR, prefixEnd + 1);
  if (blockIdInnerSep === -1) {
    return {
      isInstance: false,
      originalBlockId: blockId,
      instanceKey: undefined,
    };
  }
  const boundary = blockId.indexOf(SEPARATOR, blockIdInnerSep + 1);
  if (boundary === -1) {
    return {
      isInstance: false,
      originalBlockId: blockId,
      instanceKey: undefined,
    };
  }

  const originalBlockId = blockId.slice(prefixEnd + 1, boundary);
  const instanceKey = blockId.slice(boundary + 1);

  // Dev-only invariant. Block IDs are auto-generated as `b_<single-segment>` with
  // no internal `_` (the second-`_` boundary rule depends on this). Today's parser
  // truncates at the second `_`, so a directly-violating output is rare — but the
  // check is defense-in-depth against (a) inputs whose pre-boundary portion is not
  // shaped as `b_X` and (b) future parser changes that loosen the boundary rule.
  // Stays free in prod.
  if (process.env.NODE_ENV !== 'production' && originalBlockId.indexOf(SEPARATOR, 2) !== -1) {
    throw new Error(
      `[parseRuntimeBlockId] invariant violated: originalBlockId "${originalBlockId}" parsed from "${blockId}" contains a "_" past the \`b_\` prefix. Block IDs must be \`b_<single-segment>\` with no internal underscores.`,
    );
  }

  return {
    isInstance: true,
    originalBlockId,
    instanceKey,
  };
}
